/**
 * Generic Property Grid
 * Dynamically renders property sections based on element definitions
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ChevronDown, ChevronRight, ArrowUp, ArrowDown, ChevronsUp, ChevronsDown } from 'lucide-react';
import type { TemplateElement, AbsoluteElement } from '../../types/template.types';
import type { PropertyDefinition } from '../../element-definitions/element-definition.types';
import { elementRegistry } from '../../element-definitions/element-registry';
import { propertyRendererFactory } from './renderers/property-renderer-factory';

export interface CellData {
  horizontalAlign?: 'left' | 'center' | 'right' | 'stretch';
  verticalAlign?: 'top' | 'middle' | 'bottom' | 'stretch';
  backgroundColor?: string;
}

interface GenericPropertyGridProps {
  element?: TemplateElement | AbsoluteElement;
  cell?: CellData;
  onChange: (element: TemplateElement | AbsoluteElement | CellData) => void;
  globalFontFamily?: string;
  globalFontSize?: number;
  globalFontColor?: string;
  // Layer management callbacks for absolute-image elements
  onBringToFront?: () => void;
  onSendToBack?: () => void;
  onBringForward?: () => void;
  onSendBackward?: () => void;
  // Max height constraint for element sizing
  maxHeight?: number;
  // Special handling for header/footer containers
  isHeaderOrFooter?: boolean;
}

export function GenericPropertyGrid({
  element,
  cell,
  onChange,
  onBringToFront,
  onSendToBack,
  onBringForward,
  onSendBackward,
  maxHeight,
  isHeaderOrFooter,
}: GenericPropertyGridProps) {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  
  // Determine type from element or cell
  const type = element ? element.type : 'cell';
  const data = element || cell;
  
  if (!data) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Keine Daten verfügbar
      </div>
    );
  }
  
  const definition = elementRegistry.getDefinition(type);
  
  if (!definition) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Keine Eigenschaften verfügbar für Typ: {type}
      </div>
    );
  }

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const updateProperty = (key: string, value: unknown) => {
    console.log('GenericPropertyGrid updateProperty:', { key, value, currentData: data });
    const updated = { ...data, [key]: value } as TemplateElement | AbsoluteElement | CellData;
    console.log('GenericPropertyGrid calling onChange with:', updated);
    onChange(updated);
  };

  const updateProperties = (updates: Record<string, unknown>) => {
    console.log('GenericPropertyGrid updateProperties:', { updates, currentData: data });
    const updated = { ...data, ...updates } as TemplateElement | AbsoluteElement | CellData;
    console.log('GenericPropertyGrid calling onChange with:', updated);
    onChange(updated);
  };

  const renderProperty = (prop: PropertyDefinition) => {
    const value = (data as unknown as Record<string, unknown>)[prop.key];
    
    // Use factory to get renderer
    const renderer = propertyRendererFactory.getRenderer(prop.type);
    
    if (renderer) {
      return renderer.render({
        prop,
        value,
        updateProperty,
        updateProperties,
        element: data, // Pass the whole element for complex renderers
        maxHeight,
      });
    }
    
    // Fallback for unknown property types
    return null;
  };

  // Global section order: element-specific first, then color, size, alignment, spacing
  const getSectionPriority = (sectionId: string): number => {
    // Element-specific sections (content, table, etc.) get highest priority
    if (!['color', 'font', 'text-style', 'size', 'alignment', 'spacing', 'grid-structure'].includes(sectionId)) {
      return 0; // Highest priority
    }
    
    // Standard sections in defined order
    switch (sectionId) {
      case 'color': return 10;
      case 'font': return 20;
      case 'text-style': return 25; // Text style between font and size
      case 'size': return 30;
      case 'grid-structure': return 35;
      case 'alignment': return 40;
      case 'spacing': return 50;
      default: return 100; // Other sections at the end
    }
  };
  
  const sortedSections = [...definition.sections].sort((a, b) => {
    const priorityA = getSectionPriority(a.id);
    const priorityB = getSectionPriority(b.id);
    
    // First sort by global priority
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // Then by defined order within same priority
    return a.order - b.order;
  });

  // Show layer management for absolute-image elements
  const showLayerManagement = element && element.type === 'absolute-image' && 
    (onBringToFront || onSendToBack || onBringForward || onSendBackward);

  // Show special container notice for header/footer
  const showContainerNotice = element && element.type === 'container' && isHeaderOrFooter;

  return (
    <div className="space-y-1">
      <div className="p-4 border-b">
        <h3 className="font-semibold text-sm">{definition.label}</h3>
        {definition.description && (
          <p className="text-xs text-muted-foreground mt-1">{definition.description}</p>
        )}
      </div>

      {/* Container Notice for Header/Footer */}
      {showContainerNotice && (
        <div className="p-4 border-b bg-blue-50">
          <p className="text-xs text-muted-foreground">
            Kopf- und Fußzeilen passen ihre Höhe automatisch an den Inhalt an.
          </p>
        </div>
      )}

      {/* Layer Management Section for absolute-image */}
      {showLayerManagement && (
        <>
          <Separator />
          <div className="p-4 space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Ebenen
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {onBringToFront && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onBringToFront}
                  className="h-9 text-xs"
                >
                  <ChevronsUp className="h-4 w-4 mr-1" />
                  Ganz nach vorne
                </Button>
              )}
              {onBringForward && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onBringForward}
                  className="h-9 text-xs"
                >
                  <ArrowUp className="h-4 w-4 mr-1" />
                  Nach vorne
                </Button>
              )}
              {onSendBackward && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onSendBackward}
                  className="h-9 text-xs"
                >
                  <ArrowDown className="h-4 w-4 mr-1" />
                  Nach hinten
                </Button>
              )}
              {onSendToBack && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onSendToBack}
                  className="h-9 text-xs"
                >
                  <ChevronsDown className="h-4 w-4 mr-1" />
                  Ganz nach hinten
                </Button>
              )}
            </div>
          </div>
        </>
      )}
      
      {sortedSections.map((section, index) => {
        const isCollapsed = collapsedSections.has(section.id);
        let sectionProperties = definition.properties.filter(p => p.section === section.id);
        
        // For header/footer containers, filter out vertical alignment and height
        if (isHeaderOrFooter) {
          sectionProperties = sectionProperties.filter(p => 
            p.key !== 'verticalAlign' && p.key !== 'height'
          );
        }
        
        if (sectionProperties.length === 0) return null;
        
        return (
          <div key={section.id}>
            {index > 0 && <Separator />}
            <div className="p-4 space-y-3">
              {section.collapsible !== false ? (
                <button
                  className="flex items-center gap-2 w-full text-left"
                  onClick={() => toggleSection(section.id)}
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {section.label}
                  </h4>
                </button>
              ) : (
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {section.label}
                </h4>
              )}
              
              {!isCollapsed && (
                <div className="space-y-3">
                  {section.description && (
                    <p className="text-xs text-muted-foreground -mt-1">
                      {section.description}
                    </p>
                  )}
                  {sectionProperties.map(renderProperty)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
